import {useMemo, useState} from "react";
import {MeterEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {getFormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode"

export type UseMeterDataTableFilterProps = Omit<UseDataTableFilterProps<MeterEntity>, 'filterConfig'>

export function useMeterDataTableFilter(props: UseMeterDataTableFilterProps): UseDataTableFilterResult<MeterEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [debouncedNameQuery] = useDebouncedValue(nameQuery, 200);

  const [typeQuery, setTypeQuery] = useState<string | undefined>(undefined);
  const [debouncedTypeQuery] = useDebouncedValue(typeQuery, 200);

  const [freightTypeQuery, setFreightTypeQuery] = useState<string | undefined>(undefined);
  const [debouncedFreightTypeQuery] = useDebouncedValue(freightTypeQuery, 200);

  const [readingQuery, setReadingQuery] = useState<string | undefined>(undefined);
  const [debouncedReadingQuery] = useDebouncedValue(readingQuery, 200);

  const filterConfig: DataTableFilterConfig<MeterEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: debouncedNameQuery,
        filter: (record, query) => !record.name.toLowerCase().includes(query.toLowerCase())
      },
      "type": {
        setQuery: setTypeQuery,
        query: typeQuery,
        debouncedQuery: debouncedTypeQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.type, t)
          return !record.type.code.toLowerCase().includes(query.toLowerCase()) && ( !display || !display.toLowerCase().includes(query.toLowerCase()))
        }
      },
      "freightType": {
        setQuery: setFreightTypeQuery,
        query: freightTypeQuery,
        debouncedQuery: debouncedFreightTypeQuery,
        filter: (record, query) => !record.freightTypeName || !record.freightTypeName.toLowerCase().includes(query.toLowerCase())
      },
      "reading": {
        setQuery: setReadingQuery,
        query: readingQuery,
        debouncedQuery: debouncedReadingQuery,
        filter: (record, query) => !record.objectName || !record.objectName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    debouncedNameQuery,
    typeQuery,
    debouncedTypeQuery,
    freightTypeQuery,
    debouncedFreightTypeQuery,
    readingQuery,
    debouncedReadingQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
